<template>
    <div class="cc-product">
        <div class="cc-product__thumbnail">
            <a :href="product.link" :title="product.name" v-if="renderLink">
                <img :src="product.thumbnail" :alt="product.name" class="cc-product__image">
            </a>
            <img :src="product.thumbnail" :alt="product.name" class="cc-product__image" v-else>
        </div>
        <div class="cc-product__information">
            <div class="cc-product__controls">
                <div :title="translations['Remove product']" class="cc-control cc-control--remove-product" @click="removeProduct">&times;</div>
            </div>
            <div class="cc-product__header cc-controls">
                <a :href="product.link" :title="product.name" v-if="renderLink">
                    <div class="cc-product__title cc-title cc-title--product">{{ name }}</div>
                </a>
                <div class="cc-product__title cc-title cc-title--product" v-else>{{ name }}</div>
                <div v-for="customization in product.customization" v-bind:key="customization.key">
                  <div v-if="customization.type ==='text'" class="cc-title cc-product__customization-value">{{ customization.label }}: {{ customization.value }}</div>
                  <div v-if="customization.type ==='files'" class="cc-title cc-product__customization-value"><img :src="customization.file" /></div>
                </div>
            </div>
            <div class="cc-product__main">
              <div v-if="product.availability_type === 'none'" class="cc-availability cc-availability-none">{{ translations['This product is no longer in stock'] }}</div>
              <div v-if="product.availability_type === 'later' && product.available_later" class="cc-availability cc-availability-later">{{ product.available_later }}</div>
              <div v-if="product.availability_type === 'now' && product.available_now" class="cc-availability cc-availability-now">{{ product.available_now }}</div>
                <div class="cc-product__quantity-wrapper">
                  <div class="cc-product__quantity-label" v-if="quantityLabel">
                      {{ quantityLabel }}
                  </div>
                  <div class="cc-product__quantity" v-if="config.quantity_controls_active">
                      <product-quantity :product="product"/>
                  </div>
                  <div class="cc-product__quantity" v-else>
                      {{`${product.cart_quantity}x`}}
                  </div>
                </div>
                <div class="cc-product__attributes">
                    <div v-for="(attribute, index) in product.attributes_data" v-bind:key="index">
                      <span class="cc-product__attributes__attribute-group">{{ attribute.group }} </span>
                      <span class="cc-product__attributes__attribute-name">{{ attribute.name }}</span>
                    </div>
                    <div v-if="calculatedValue" class="cc-product__calculated-attribute">
                      <span class="cc-product__calculated-attribute__title">{{ translations['Calculated value'] }} </span>
                      <span class="cc-product__calculated-attribute__value">{{ calculatedValue }}</span>
                    </div>
                </div>
                <div class="cc-product__prices">
                    <div class="cc-price cc-price--product-original" v-if="price !== originalPrice && config.theme_settings.show_original_price">
                      <span class="cc-price cc-price--product-original-label">{{ translations['Original price'] }}</span>
                      <span class="cc-price cc-price--product-original-value">{{ originalPrice }}</span>
                    </div>
                    <div class="cc-price cc-price--product-discount" v-if="price !== originalPrice && config.theme_settings.show_original_price">
                      <span class="cc-price cc-price--product-discount-label">{{ translations['Discount'] }}</span>
                      <span class="cc-price cc-price--product-discount-value">-{{ discountPrice }}</span>
                    </div>
                    <div class="cc-price cc-price--product">
                      <span class="cc-price cc-price--product-label">{{ translations['Your price'] }}</span>
                      <span class="cc-price cc-price--product-value">{{ price }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState} from 'vuex';
    import ProductQuantity from "./components/ProductQuantity";

    export default {
        components: {
            ProductQuantity,
        },
        computed: {
            ...mapState({
                translations: state => state.translations,
                config: state => state.config,
                useVat: state => state.config.use_vat,
                selectedCarrier: state => state.selectedCarrier,
            }),
            renderLink: function () {
                return this.selectedCarrier === null;
            },
            name: function () {
                if (this.config.trim_product_name && this.product.name.length > 50) {
                    return this.product.name.trim().substring(0, 50) + '...';
                }

                return this.product.name;
            },
            attributes: function () {
                return this.product.attributes.split(', ');
            },
            price: function () {
                if (this.product.is_gift) {
                    return this.translations['Free'];
                }
                if (this.useVat) {
                    return this.product.formattedPrices.total_with_tax;
                }
                return this.product.formattedPrices.total_without_tax;
            },
            originalPrice: function () {
                if (this.useVat) {
                    return this.product.formattedPrices.total_price_without_reduction_with_tax;
                }
                return this.product.formattedPrices.total_price_with_reduction_without_tax;
            },
            discountPrice: function () {
              if (this.useVat) {
                return this.product.formattedPrices.price_reduction_with_tax;
              }
              return this.product.formattedPrices.price_reduction_without_tax;
            },
            calculatedValue: function () {
              if (!(this.product.id_category_default in this.config.theme_settings.custom_calculation_by_category_and_attribute)) {
                return '';
              }
              const attributeId = this.config.theme_settings.custom_calculation_by_category_and_attribute[this.product.id_category_default]['id_attribute'];
              if (!(attributeId in this.product.attributes_data)) {
                return '';
              }
              const attributeValue = this.product.attributes_data[attributeId]['name'];
              const stringToReplace = this.config.theme_settings.custom_calculation_by_category_and_attribute[this.product.id_category_default]['string_to_remove'];
              const valueX = parseFloat(attributeValue.replace(stringToReplace, '').trim());
              const valueY = parseFloat(this.product.cart_quantity);

              const append = this.config.theme_settings.custom_calculation_by_category_and_attribute[this.product.id_category_default]['string_to_append'];
              const round = parseInt(this.config.theme_settings.custom_calculation_by_category_and_attribute[this.product.id_category_default]['round']);
              const roundFunction = function(number, places) {
                return +(Math.round(number + "e+" + places)  + "e-" + places);
              }

              return parseFloat(roundFunction(valueX * valueY, round)).toFixed(round) + append;
            },
            quantityLabel: function () {
              if (this.product.id_category_default in this.config.theme_settings.custom_unit_by_category) {
                return this.config.theme_settings.custom_unit_by_category[this.product.id_category_default]['title'];
              }
              if (this.config.theme_settings.custom_unit_by_category[0] !== undefined) {
                return this.config.theme_settings.custom_unit_by_category[0]['title'];
              }
              return '';
            }
        },
        methods: {
            removeProduct: function () {
                this.$store.dispatch('products/REMOVE', {
                    id: this.product.id,
                    id_attribute: this.product.id_attribute,
                    id_customization: this.product.id_customization
                });
            },
        },
        props: [
            'product',
        ],
    };
</script>

<style lang="scss" scoped="scoped">
    @import "./scss/components/product";
    .cc-availability {
      width: 100%;
      padding: 0.5rem 1rem 0;
      &.cc-availability-none {
        color: #8e0000;
      }
      &.cc-availability-later {
        color: #00009a;
      }
      &.cc-availability-now {
        color: #00a300;
      }
    }
    .cc-product__prices {
      .cc-price--product-original {
        font-size: 14px;
        font-weight: normal;
        text-decoration: line-through;
        width: 100%;
        text-align: right;
      }
      .cc-price--product-discount {
        width: 100%;
        text-align: right;
      }
      .cc-price.cc-price--product-label {
        display: none;
      }
    }
    .cc-product__quantity-wrapper {
      width: 100%;
    }
</style>
